/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { Item } from "types/models/menu"
import { useMartItemMenuDisplayV2Query } from "queries"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { mapItemsV2ToItems } from "utils/menuV3Mapper"
import { isEmpty } from "lodash"
import { isItemWithoutPhotoMode, isMenuFilterActive } from "utils/menu_helper"

interface UseMenuOverviewItemsReturn {
  items: Item[]
  filteredItems: Item[]
  isLoading: boolean
  isFetching: boolean
}

export const useMenuOverviewItems = (): UseMenuOverviewItemsReturn => {
  const enablePartialMenu = useEnablePartialMenu()

  // Get data from Redux
  const {
    merchantID,
    merchantGroupID,
    isMartDepartmentStructure,

    overview: {
      items: reduxSelectedItems,
      category: selectedCategory,
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
      itemID,
      sellingTime,
    },
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  // Check if filters are active (need data for filtering)
  const isFilterActive = isMenuFilterActive(itemFilter, sellingTime)

  // Use martItemMenuDisplayV2 query only when:
  // 1. enablePartialMenu is true
  // 2. Filters are active (otherwise V3 components handle their own data)
  const {
    data: martMenuData,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
  } = useMartItemMenuDisplayV2Query(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !merchantID || !isFilterActive,
    },
  )

  // Get items from query or Redux (before filtering)
  const items = useMemo(() => {
    if (enablePartialMenu && martMenuData) {
      if (isMartDepartmentStructure && selectedSubDepartment) {
        // Find the selected department and sub-department in the mart menu data
        const department = martMenuData.departments?.find(
          dept => dept.itemClassID === selectedDepartment?.itemClassID,
        )
        const subDepartment = department?.subDepartments?.find(
          subDept => subDept.itemClassID === selectedSubDepartment.itemClassID,
        )
        if (subDepartment?.items) {
          return mapItemsV2ToItems(subDepartment.items)
        }
      } else if (!isMartDepartmentStructure && selectedCategory) {
        // For non-mart structure, find category in departments (categories are stored as departments in mart API)
        // Note: This might need adjustment based on actual API structure
        const category = martMenuData.departments?.find(
          dept => dept.itemClassID === selectedCategory.categoryID,
        )
        if (category?.items) {
          return mapItemsV2ToItems(category.items)
        }
      }
      return []
    }
    return reduxSelectedItems
  }, [
    enablePartialMenu,
    martMenuData,
    isMartDepartmentStructure,
    selectedCategory,
    selectedDepartment,
    selectedSubDepartment,
    reduxSelectedItems,
  ])

  // Filter items by itemID, selling time, and item without photo
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      let flag = isEmpty(itemID) ? true : item.itemID === itemID

      // Filter by selling time (only needed when using query, Redux already filters)
      if (flag && sellingTime && enablePartialMenu && martMenuData) {
        flag = item.sellingTimeID === sellingTime.sellingTimeID
      }

      // Filter by item without photo
      if (flag && isItemWithoutPhotoMode(itemFilter)) {
        flag = !(item.imageURL || (item.imageURLs && item.imageURLs.length > 0))
      }
      return flag
    })
  }, [items, itemID, sellingTime, enablePartialMenu, martMenuData, itemFilter])

  const isLoading =
    enablePartialMenu && (isQueryLoading || (isQueryFetching && !martMenuData))

  return {
    items,
    filteredItems,
    isLoading: !!isLoading,
    isFetching: enablePartialMenu ? isQueryFetching : false,
  }
}
